import React, { useEffect, useMemo, useRef, useContext } from 'react';
import CareerPathwayCard from '../../CareerPathwayPlanner/components/CareerPathwayCard';
import { RecommendedSectionProps } from '../types';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import ViewMoreExpandable from '@/components/DesignSystem/EditorialListings/ViewMoreExpandable';
import { MobiusContext } from '@/components/MobiusProvider';
import getUrlQuery from '@/utils/getUrlQuery';
import getTrafficSource from '@/utils/getTrafficSource';
import { useCalculatorContext } from '@/components/DesignSystem/Subsections/Calculator/contexts';
import { useTrackingContext } from '@/components/SonicEditorialListings/useTrackingContext';

const RecommendedSection = ({
  recommendedItems,
  title,
  subtitle,
  orientation = 'vertical',
  correlationId,
  studyMatchDestination,
  showViewMore = true,
  eventContext = 'career-pathways',
}: RecommendedSectionProps) => {
  const { siteConfig } = useContext(MobiusContext);

  // Create a stable string representation of product IDs to use as dependency
  const productIdsKey = useMemo(() => {
    return recommendedItems
      .map((item) => item.id?.toString() || '')
      .sort()
      .join(',');
  }, [recommendedItems]);

  // Create a stable mapping of viewCorrelationId for each item
  const viewCorrelationIds = useMemo(() => {
    return recommendedItems.reduce(
      (acc, item) => {
        acc[item.id?.toString() || ''] = uuid();
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [productIdsKey]); // Use productIdsKey instead of recommendedItems

  // Track which products have been viewed to prevent duplicate events
  const viewedProductsRef = useRef<Set<string>>(new Set());
  const lastProductIdsKeyRef = useRef<string>('');

  const { config, getCalculatorValue } = useCalculatorContext();
  // Get tracking context using the custom hook
  const trackingContext = useTrackingContext({
    initialContext: {
      formatSubtype: config?.tracking?.cardformatSubtype || '',
      formatType: 'dexp-tool-results',
    },
  });

  // Get category, subject (name), and degree (variant) from calculator context
  const categoryValue = getCalculatorValue('category') || 'general';
  const subjectValue = getCalculatorValue('subject') || 'general';
  const degreeValue = getCalculatorValue('degree') || 'general';

  const formatSubtype = config?.tracking?.cardformatSubtype || '';
  const formatType = 'dexp-tool-results';
  const location = config?.tracking?.location || '';
  const viewCorrelationId = uuid();

  useEffect(() => {
    // If the form has been updated (productIdsKey changed), reset viewed products tracking
    if (lastProductIdsKeyRef.current !== productIdsKey) {
      viewedProductsRef.current.clear();
      lastProductIdsKeyRef.current = productIdsKey;
    }

    recommendedItems.forEach((item, index) => {
      const productId = item.id?.toString() || '';

      // Only fire ProductViewed event if this product hasn't been viewed yet
      if (!viewedProductsRef.current.has(productId)) {
        const itemViewCorrelationId = viewCorrelationIds[productId];

        tagularEvent('ProductViewed', {
          viewCorrelationId: itemViewCorrelationId,
          product: {
            brand: item.subheader || '', // School name
            category: categoryValue, // Category from calculator context
            formatSubtype: formatSubtype,
            formatType: formatType,
            location: location,
            name: subjectValue, // Subject from calculator context
            variant: degreeValue, // Degree from calculator context
            productId: productId,
            positionEngine: (index + 1).toString(),
          },
        });

        // Mark this product as viewed
        viewedProductsRef.current.add(productId);
      }
    });
  }, [
    recommendedItems,
    viewCorrelationIds,
    categoryValue,
    subjectValue,
    degreeValue,
    formatSubtype,
    formatType,
    location,
  ]);

  const handleProgramClick = (item: any, index: number, baseUrl: string) => {
    const itemViewCorrelationId = viewCorrelationIds[item.id?.toString() || ''];

    // Build final URL with same correlationId as event
    let finalDestinationUrl = baseUrl;
    if (trackingContext) {
      const { domain } = siteConfig || {};
      finalDestinationUrl = getUrlQuery(baseUrl, {
        publisher: domain,
      });
    }

    tagularEvent('ProductClicked', {
      viewCorrelationId: itemViewCorrelationId,
      correlationId:
        finalDestinationUrl.split('correlationId=')[1]?.split('&')[0] ||
        correlationId,
      outboundUrl: finalDestinationUrl,
      product: {
        brand: item.subheader || 'general', // School name
        category: categoryValue, // Category from calculator context
        formatSubtype: formatSubtype,
        formatType: formatType,
        location: location,
        name: subjectValue, // Subject from calculator context
        position: index + 1, // Position in the list (1-based)
        productId: item.id?.toString() || '',
        variant: degreeValue, // Degree from calculator context
        positionEngine: (index + 1).toString(),
      },
    });

    window.open(finalDestinationUrl, '_blank');
  };

  // Fire Career Pathway specific ElementViewed event for the View More button
  const viewMoreButtonRef = useRef<HTMLDivElement>(null);
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (!hasViewedRef.current && viewMoreButtonRef.current) {
      // Fire ElementViewed event with Career Pathway context
      tagularEvent('ElementViewed', {
        viewCorrelationId: viewCorrelationId,
        correlationId,
        webElement: {
          elementType: 'link',
          text: 'View More Programs',
          htmlId: 'view-more-button',
          name: 'view-more-button',
          location: 'career-pathways',
          position: 'career-pathways',
        },
      });
      hasViewedRef.current = true;
    }
  }, []);

  return (
    <div className='bg-white rounded-xl flex flex-col w-full sticky top-[100px]'>
      <span className='text-base md:text-lg font-bold text-gray-900 pb-2'>
        {title}
      </span>
      {subtitle && (
        <span className='text-gray-600 pb-2 md:pb-6 text-xs md:text-sm font-normal'>
          For {subtitle}
        </span>
      )}
      <div
        className={` ${
          orientation === 'horizontal'
            ? 'flex flex-col md:flex-row gap-2.5 md:overflow-x-auto'
            : 'grid grid-cols-1 gap-5'
        }`}
      >
        {recommendedItems.map((item, index) => (
          <div
            key={item.id}
            className={`${
              orientation === 'horizontal'
                ? 'md:w-[250px] flex-shrink-0'
                : 'w-full'
            } last:mb-4`}
          >
            <CareerPathwayCard
              key={item.id}
              subheader={item.subheader}
              title={item.title}
              description={item.description}
              destinationUrl={item.destinationUrl}
              correlationId={correlationId}
              logo={item.logo}
              onProgramClick={() =>
                handleProgramClick(item, index, item.destinationUrl || '')
              }
            />
          </div>
        ))}
      </div>
      {showViewMore && (
        <div className='pt-2 md:pt-6' ref={viewMoreButtonRef}>
          <ViewMoreExpandable
            url={studyMatchDestination}
            onClick={() => {}}
            label='View More Programs'
            shouldLinkOut={true}
            location='career-pathways'
            position='career-pathways'
            inheritedCorrelationId={viewCorrelationId}
            isDexpTool={true}
          />
        </div>
      )}
    </div>
  );
};

export default RecommendedSection;
